<template>
  <v-menu offset-y :close-on-content-click="false" :attach="true">
    <template v-slot:activator="{ on, attrs }">
      <v-btn
        :class="{ background: $attrs.isActive }"
        icon
        small
        v-bind="{ ...$attrs, attrs, ...$props }"
        v-on="{ ...on, ...$listeners }"
        :disabled="!isModuleConfigured(name)"
        color="rgba(0,0,0, 0.7)"
        @click="checkActiveTools"
      >
        <v-icon size="19px">
          {{ icon }}
        </v-icon>
      </v-btn>
    </template>
    <v-list>
      <v-list-item>
        <data-input
          :menu-props="{ contentClass: 'menu-max-width-content' }"
          :data-type="{ name: 'autocomplete' }"
          style="min-width: 220px;"
          class="pa-0 ma-0 text-body-2 mt-2"
          :prepend-icon="icon"
          append-icon=""
          :label="label"
          color="rgba(0,0,0,0.7)"
          :items="results"
          item-text="description"
          :item-value="'geometry_wkt' || 'description'"
          v-model="selected"
          :search-input.sync="query"
          @update:search-input="search"
          :filter="computedFilterFunction"
          @change="zoomTo"
          clearable
          :hint="hint"
          return-object
          cache-items
          :hide-details="isAddressFromSystem ? true : this.layer === 'parcels'"
          hide-no-data
        >
          <template #item="{parent, item, on, attrs }">
            <v-list-item v-on="on" v-bind="attrs">
              <v-list-item-content>
                <v-list-item-title v-html="parent.genFilteredText(item.description)" />
                <italic-subheader
                  v-if="item.jednostka"
                  :straight-text="formattedSubinfos(item.jednostka)"
                  style="font-size: 11px !important;"
                />
              </v-list-item-content>
            </v-list-item>
          </template>
        </data-input>
      </v-list-item>
    </v-list>
  </v-menu>
</template>

<script>
import { debounce } from 'lodash';
import { call, get } from 'vuex-pathify';
import DataInput from '@/components/DataInput';
import ItalicSubheader from '@/components/ItalicSubheader';

export default {
  name: 'TheNavbarToolsPanelSearch',
  components: {
    DataInput,
    ItalicSubheader,
  },
  props: {
    layer: {
      type: String,
      required: true,
    },
    name: {
      type: String,
      required: true,
    },
    icon: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    query: null,
    selected: undefined,
    isLoaded: false,
    results: [],
  }),
  computed: {
    activeTool: get('tools/activeTool'),
    addressesMapping: get('admin/modulesMapping@addresses'),
    parcelsMapping: get('admin/modulesMapping@parcels'),
    settings: get('admin/settingsValues'),
    isSearchAddressConfigured() {
      if (this.settings?.addresses_provider === 'gugik_api') {
        return true;
      } else {
        const elements = [
          'attribute_place',
          'attribute_postal_code',
          'attribute_street',
          'attribute_number',
          'addresses_datasource',
        ];
        return elements.every(element => this.addressesMapping[element]);
      }
    },
    isSearchParcelConfigured() {
      if (this.settings?.parcels_provider === 'uldk') {
        return true;
      } else {
        const elements = ['attribute_community', 'attribute_region', 'attribute_number', 'parcels_datasource'];
        return elements.every(element => this.parcelsMapping[element]);
      }
    },
    isAddressFromSystem() {
      return this.layer === 'address_points' && this.settings?.addresses_provider === 'system';
    },
    label() {
      return this.$i18n.t(`navbar.${this.name}`);
    },
    hint() {
      return this.isAddressFromSystem
        ? ''
        : this.layer === 'parcels'
        ? ''
        : this.$i18n.t('admin.addressProviderGugikHint');
    },
    suffixedLayer() {
      return `${this.layer}_uploaded`;
    },
    computedFilterFunction() {
      return this.layer === 'parcels' ? this.filterFunction : undefined;
    },
  },
  methods: {
    getDataSourceFeatures: call('layers/getDataSourceFeatures'),
    searchByLayer: call('search/searchByLayer'),
    searchParcelsByLayer: call('search/searchParcelsByLayer'),
    searchAddress: call('addresses/searchAddress'),
    searchGugik: call('addresses/searchGugik'),
    async search(query) {
      if (!query) {
        return;
      }
      try {
        if (this.layer === 'parcels') {
          const r = await this.searchParcelsByLayer({ params: { search_text: query, limit: 200 } });
          this.results = r.data.data;
        } else if (this.isAddressFromSystem) {
          const r = await this.searchAddress({ params: { search_text: query, whole_word: false } });
          this.results = r.data.data;
        } else if (this.layer === 'address_points' && this.settings?.addresses_provider === 'gugik_api') {
          this.results = query.length > 2 ? await this.searchGugik(query) : [];
        } else {
          const r = await this.searchByLayer({ layer: this.layer, params: { search_text: query, whole_word: false } });
          this.results = r.data.data;
        }
      } catch (error) {
        console.log('~ error', error);
      }
    },
    zoomTo(selected) {
      if (!selected) {
        this.$root.$emit('removeMarker', this.layer);
        return;
      }
      this.$root.$emit('createGeomAndFitView', selected.geom || selected.geometry, {
        isDrawMarker: true,
        layerId: this.layer,
      });
    },
    filterFunction(item, queryText, itemText) {
      return itemText.startsWith(queryText);
    },
    formattedSubinfos(text) {
      return text.replaceAll(/{|}/g, '').replaceAll(',', ', ');
    },
    isModuleConfigured(name) {
      return this[`is${this.$_capitalize(name)}Configured`];
    },
    checkActiveTools() {
      if (this.activeTool) {
        this.$root.$emit('deactivateAllTools');
      }
    },
  },
  created() {
    // https://forum.vuejs.org/t/lodash-debounce-not-working-when-placed-inside-a-method/86334/4
    this.search = debounce(this.search, 200);
  },
};
</script>

<style lang="scss" scoped>
::v-deep {
  .v-input__icon {
    i {
      font-size: 21px !important;
    }
    button {
      font-size: 21px !important;
    }
  }
  .v-select__slot {
    label {
      font-size: 14px;
    }
  }
  .v-list-item__title {
    text-align: left;
    font-family: Roboto;
    font-style: normal;
    font-weight: normal;
    font-size: 14px;
    line-height: 15px;
  }
  .v-text-field.v-text-field--enclosed .v-text-field__details {
    margin-bottom: 0 !important;
  }
}
</style>
